import { useEffect, useMemo } from "react";
import type { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";

import {
  type DocumentTypeCategory,
  type DocumentTypeCode,
  documentTypesFor,
} from "../constants/identityDocumentTypes.constants";

/**
 * Keeps a document-type field in sync with the country field that scopes it.
 *
 * The backend validates the document type against a country (`@IsValidDocumentType`), so a type that was
 * valid for one country becomes a 400 for the next. This returns the codes valid for the current country and
 * resets the field whenever the selection no longer belongs to that list — a selection that is still valid
 * (e.g. `CE`, shared by COL and PER) is preserved so switching country does not discard the user's answer.
 *
 * Returns codes rather than ready-made select options: the caller owns the i18n labels, which keeps this hook
 * free of a callback whose identity would have to be memoized.
 *
 * Pass `numberField` to have the number typed for the old document go with it. Without that, resetting the
 * type alone leaves a document number that now claims to be something it is not — a Colombian cédula stored
 * as a Brazilian RG passes every client and server check, because the backend validates the character set and
 * never the semantics.
 */
export function useCountryScopedDocumentType<
  TFieldValues extends FieldValues,
  TContext,
  TTransformed extends FieldValues,
>(
  form: UseFormReturn<TFieldValues, TContext, TTransformed>,
  countryField: Path<TFieldValues>,
  typeField: Path<TFieldValues>,
  category: DocumentTypeCategory,
  numberField?: Path<TFieldValues>,
): readonly DocumentTypeCode[] {
  const country = form.watch(countryField);
  const countryCode = typeof country === "string" ? country : undefined;

  const codes = useMemo(() => documentTypesFor(countryCode, category), [countryCode, category]);

  useEffect(() => {
    const current = form.getValues(typeField);
    if (typeof current === "string" && (codes as readonly string[]).includes(current)) return;
    // `documentTypesFor` always falls back to the generic list, so `[0]` exists.
    form.setValue(typeField, codes[0] as PathValue<TFieldValues, Path<TFieldValues>>, {
      shouldValidate: false,
    });
    if (!numberField) return;
    form.setValue(numberField, "" as PathValue<TFieldValues, Path<TFieldValues>>, {
      shouldValidate: false,
    });
    // The error belonged to the document that just went away. `shouldValidate: false` keeps the emptied
    // field from turning red on its own, but it also leaves the old message pinned underneath it.
    form.clearErrors(numberField);
  }, [codes, form, typeField, numberField]);

  return codes;
}
